import React, { useState, useEffect } from 'react';
import './Qualifications.css';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

const TAB_PERMISSION_MAP = {
  'referee-qualifications': { pageKey: 'admin', sectionKey: 'referee-qualifications' },
  'bench-qualifications': { pageKey: 'admin', sectionKey: 'bench-qualifications' },
  'court-qualifications': { pageKey: 'admin', sectionKey: 'court-qualifications' },
  'division-mappings': { pageKey: 'admin', sectionKey: 'division-age-groups' },
  'coach-qualifications': { pageKey: 'admin', sectionKey: 'coach-qualifications' },
};

// Pay schema collapsed 16 May 2026 to 6 referee bands (+ 2 bench bands).
// The Division → Age Group dropdown now offers one option per pay group
// instead of the old 28 granular divisions. Each value is a canonical
// age-group string that the backend resolvers (refBandForAgeGroup /
// benchBandForAgeGroup) map to the right band, so saved mappings stay
// compatible.
const PAY_GROUP_OPTIONS = [
  { value: 'U10 ALL',              label: 'Juniors — U10 / U12 / U14' },
  { value: 'U16 Div 2',            label: 'Youth Development — U16 / U18 (Div 2 & 3)' },
  { value: 'U16 Championship',     label: 'Youth Championship — U16 / U18 (Div 1 / Championship)' },
  { value: 'Seniors Div 1',        label: 'Seniors' },
  { value: 'Seniors Championship', label: 'Seniors Championship' },
  { value: 'Social Grade 1',       label: 'Social' },
];

// Normalise any legacy / granular age-group string onto its pay-group
// representative so the dropdown reflects the collapsed pay schema even
// for mappings saved under the old 28-division scheme. Mirrors the
// backend's refBandForAgeGroup banding logic.
const payGroupValue = (ag) => {
  const a = String(ag || '').replace(/\s+/g, '').toLowerCase();
  if (!a) return 'Social Grade 1';
  if (a.startsWith('u10') || a.startsWith('u12') || a.startsWith('u14')) return 'U10 ALL';
  if (a.startsWith('u16') || a.startsWith('u18')) {
    return (a.includes('div2') || a.includes('div3')) ? 'U16 Div 2' : 'U16 Championship';
  }
  if (a.startsWith('seniors')) return a.includes('champ') ? 'Seniors Championship' : 'Seniors Div 1';
  if (a.startsWith('social')) return 'Social Grade 1';
  return 'Social Grade 1';
};

export default function Qualifications() {
  const { canView } = usePermissions();
  const [activeTab, setActiveTab] = useState('');

  // Pay bands (collapsed 16 May 2026 from 28 per-division columns).
  // The `col` is the actual DB column name — the backend now accepts
  // payRates keyed directly by these. Blank = no rate = can't be
  // assigned to that band (enforced by the AI rules).
  const REF_BANDS = [
    { col: 'payJuniors',      label: 'U10 / U12 / U14 (all divisions)' },
    { col: 'payYouthDev',     label: 'U16 Div 2 & 3 / U18 Div 2 & 3' },
    { col: 'payYouthChamp',   label: 'U16 Div 1 & Championship / U18 Div 1 & Championship' },
    { col: 'paySeniors',      label: 'Seniors Div 1-5' },
    { col: 'payChampSeniors', label: 'Championship Seniors' },
    { col: 'paySocial',       label: 'Social (all grades)' },
  ];
  const BENCH_BANDS = [
    { col: 'payJuniorGames', label: 'Junior games (U10-U18)' },
    { col: 'paySeniorGames', label: 'Senior games (Seniors / Social)' },
  ];

  const createEmptyPayRates = (bands) => {
    const rates = {};
    (bands || REF_BANDS).forEach(b => { rates[b.col] = ''; });
    return rates;
  };

  // Referee Qualifications state
  const [refereeQualifications, setRefereeQualifications] = useState([]);
  const [refQualForm, setRefQualForm] = useState({ level: '', description: '', payRates: createEmptyPayRates() });
  const [editingRefQualId, setEditingRefQualId] = useState(null);

  // Bench Official Qualifications state
  const [benchQualifications, setBenchQualifications] = useState([]);
  const [benchQualForm, setBenchQualForm] = useState({ level: '', description: '', payRates: createEmptyPayRates(BENCH_BANDS) });
  const [editingBenchQualId, setEditingBenchQualId] = useState(null);

  // Court Official Qualifications state
  const [courtQualifications, setCourtQualifications] = useState([]);
  const [courtQualForm, setCourtQualForm] = useState({ level: '', description: '', pay: '' });
  const [editingCourtQualId, setEditingCourtQualId] = useState(null);

  // Coach Qualifications state
  const [coachQualifications, setCoachQualifications] = useState([]);
  const [coachQualForm, setCoachQualForm] = useState({ level: '', description: '', payPerHour: '' });
  const [editingCoachQualId, setEditingCoachQualId] = useState(null);

  // Form visibility state
  const [showRefForm, setShowRefForm] = useState(false);
  const [showBenchForm, setShowBenchForm] = useState(false);
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [showCoachForm, setShowCoachForm] = useState(false);

  // Division Mappings state
  const [divisions, setDivisions] = useState([]);
  const [divisionMappings, setDivisionMappings] = useState({});
  const [divisionSaveStatus, setDivisionSaveStatus] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Archive toggle state
  const [showArchivedRefQuals, setShowArchivedRefQuals] = useState(false);
  const [showArchivedBenchQuals, setShowArchivedBenchQuals] = useState(false);
  const [showArchivedCourtQuals, setShowArchivedCourtQuals] = useState(false);
  const [showArchivedCoachQuals, setShowArchivedCoachQuals] = useState(false);

  // Set default active tab to first visible tab
  useEffect(() => {
    if (!activeTab) {
      const firstVisibleTab = Object.keys(TAB_PERMISSION_MAP).find(
        tabKey => canView(TAB_PERMISSION_MAP[tabKey].pageKey, TAB_PERMISSION_MAP[tabKey].sectionKey)
      );
      if (firstVisibleTab) setActiveTab(firstVisibleTab);
    }
  }, [activeTab, canView]);

  // Load data on mount and when archive toggles change
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchivedRefQuals, showArchivedBenchQuals, showArchivedCourtQuals, showArchivedCoachQuals]);

  const loadData = async () => {
    setLoading(true);
    try {
      const refUrl = showArchivedRefQuals ? `${API_BASE}/referee-qualifications?includeArchived=true` : `${API_BASE}/referee-qualifications`;
      const benchUrl = showArchivedBenchQuals ? `${API_BASE}/bench-official-qualifications?includeArchived=true` : `${API_BASE}/bench-official-qualifications`;
      const courtUrl = showArchivedCourtQuals ? `${API_BASE}/court-official-qualifications?includeArchived=true` : `${API_BASE}/court-official-qualifications`;
      const coachUrl = showArchivedCoachQuals ? `${API_BASE}/coach-qualifications?includeArchived=true` : `${API_BASE}/coach-qualifications`;
      const [refQualsRes, benchQualsRes, courtQualsRes, coachQualsRes, divisionsRes, mappingsRes] = await Promise.all([
        fetch(refUrl),
        fetch(benchUrl),
        fetch(courtUrl),
        fetch(coachUrl),
        fetch(`${API_BASE}/divisions`),
        fetch(`${API_BASE}/division-mappings`)
      ]);

      if (refQualsRes.ok) setRefereeQualifications(await refQualsRes.json());
      if (benchQualsRes.ok) setBenchQualifications(await benchQualsRes.json());
      if (courtQualsRes.ok) setCourtQualifications(await courtQualsRes.json());
      if (coachQualsRes.ok) setCoachQualifications(await coachQualsRes.json());
      if (divisionsRes.ok) {
        const data = await divisionsRes.json();
        setDivisions(data.divisions);
      }
      if (mappingsRes.ok) {
        const data = await mappingsRes.json();
        const mappingMap = {};
        data.mappings.forEach(m => {
          mappingMap[m.divisionName] = m.ageGroup;
        });
        setDivisionMappings(mappingMap);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== REFEREE QUALIFICATIONS ====================

  const handleAddRefQualification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingRefQualId ? `${API_BASE}/referee-qualifications/${editingRefQualId}` : `${API_BASE}/referee-qualifications`;
      const method = editingRefQualId ? 'PUT' : 'POST';

      // Send '' as null so a blank band means "no rate / can't be
      // assigned" rather than $0 (which would still be assignable).
      const payRates = {};
      REF_BANDS.forEach(b => {
        const v = refQualForm.payRates[b.col];
        payRates[b.col] = (v === '' || v == null) ? null : (parseFloat(v) || 0);
      });

      const response = await auditFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: refQualForm.level,
          description: refQualForm.description,
          payRates: payRates
        })
      });

      if (response.ok) {
        setSuccess(editingRefQualId ? 'Referee qualification updated successfully!' : 'Referee qualification added successfully!');
        setRefQualForm({ level: '', description: '', payRates: createEmptyPayRates() });
        setEditingRefQualId(null);
        setShowRefForm(false);
        loadData();
      } else {
        setError(editingRefQualId ? 'Failed to update referee qualification' : 'Failed to add referee qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditRefQualification = (qual) => {
    const payRates = {};
    REF_BANDS.forEach(b => {
      payRates[b.col] = (qual[b.col] == null) ? '' : qual[b.col];
    });
    setRefQualForm({ level: qual.level, description: qual.description, payRates: payRates });
    setEditingRefQualId(qual.id);
    setShowRefForm(true);
  };

  const handleCancelEditRefQualification = () => {
    setEditingRefQualId(null);
    setRefQualForm({ level: '', description: '', payRates: createEmptyPayRates() });
    setShowRefForm(false);
  };

  const handleArchiveRefQualification = async (id) => {
    if (!window.confirm('Archive this referee qualification? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/referee-qualifications/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Referee qualification archived!');
        loadData();
      } else {
        setError('Failed to archive referee qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== BENCH OFFICIAL QUALIFICATIONS ====================

  const handleAddBenchQualification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingBenchQualId ? `${API_BASE}/bench-official-qualifications/${editingBenchQualId}` : `${API_BASE}/bench-official-qualifications`;
      const method = editingBenchQualId ? 'PUT' : 'POST';

      const payRates = {};
      BENCH_BANDS.forEach(b => {
        const v = benchQualForm.payRates[b.col];
        payRates[b.col] = (v === '' || v == null) ? null : (parseFloat(v) || 0);
      });

      const response = await auditFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: benchQualForm.level,
          description: benchQualForm.description,
          payRates: payRates
        })
      });

      if (response.ok) {
        setSuccess(editingBenchQualId ? 'Bench official qualification updated successfully!' : 'Bench official qualification added successfully!');
        setBenchQualForm({ level: '', description: '', payRates: createEmptyPayRates(BENCH_BANDS) });
        setEditingBenchQualId(null);
        setShowBenchForm(false);
        loadData();
      } else {
        setError(editingBenchQualId ? 'Failed to update bench official qualification' : 'Failed to add bench official qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditBenchQualification = (qual) => {
    const payRates = {};
    BENCH_BANDS.forEach(b => {
      payRates[b.col] = (qual[b.col] == null) ? '' : qual[b.col];
    });
    setBenchQualForm({ level: qual.level, description: qual.description, payRates: payRates });
    setEditingBenchQualId(qual.id);
    setShowBenchForm(true);
  };

  const handleCancelEditBenchQualification = () => {
    setEditingBenchQualId(null);
    setBenchQualForm({ level: '', description: '', payRates: createEmptyPayRates(BENCH_BANDS) });
    setShowBenchForm(false);
  };

  const handleArchiveBenchQualification = async (id) => {
    if (!window.confirm('Archive this bench official qualification? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/bench-official-qualifications/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Bench official qualification archived!');
        loadData();
      } else {
        setError('Failed to archive bench official qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== COURT OFFICIAL QUALIFICATIONS ====================

  const handleAddCourtQualification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingCourtQualId ? `${API_BASE}/court-official-qualifications/${editingCourtQualId}` : `${API_BASE}/court-official-qualifications`;
      const method = editingCourtQualId ? 'PUT' : 'POST';

      const response = await auditFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: courtQualForm.level,
          description: courtQualForm.description,
          pay: parseFloat(courtQualForm.pay) || 0
        })
      });

      if (response.ok) {
        setSuccess(editingCourtQualId ? 'Court official qualification updated successfully!' : 'Court official qualification added successfully!');
        setCourtQualForm({ level: '', description: '', pay: '' });
        setEditingCourtQualId(null);
        setShowCourtForm(false);
        loadData();
      } else {
        setError(editingCourtQualId ? 'Failed to update court official qualification' : 'Failed to add court official qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditCourtQualification = (qual) => {
    setCourtQualForm({ level: qual.level, description: qual.description, pay: qual.pay || '' });
    setEditingCourtQualId(qual.id);
    setShowCourtForm(true);
  };

  const handleCancelEditCourtQualification = () => {
    setEditingCourtQualId(null);
    setCourtQualForm({ level: '', description: '', pay: '' });
    setShowCourtForm(false);
  };

  const handleArchiveCourtQualification = async (id) => {
    if (!window.confirm('Archive this court official qualification? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/court-official-qualifications/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Court official qualification archived!');
        loadData();
      } else {
        setError('Failed to archive court official qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== COACH QUALIFICATIONS ====================

  const handleAddCoachQualification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const url = editingCoachQualId ? `${API_BASE}/coach-qualifications/${editingCoachQualId}` : `${API_BASE}/coach-qualifications`;
      const method = editingCoachQualId ? 'PUT' : 'POST';
      const response = await auditFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: coachQualForm.level,
          description: coachQualForm.description,
          payPerHour: parseFloat(coachQualForm.payPerHour) || 0
        })
      });
      if (response.ok) {
        setSuccess(editingCoachQualId ? 'Coach qualification updated!' : 'Coach qualification added!');
        setCoachQualForm({ level: '', description: '', payPerHour: '' });
        setEditingCoachQualId(null);
        setShowCoachForm(false);
        loadData();
      } else {
        setError('Failed to save coach qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleEditCoachQualification = (qual) => {
    setCoachQualForm({ level: qual.level, description: qual.description, payPerHour: qual.payPerHour || '' });
    setEditingCoachQualId(qual.id);
    setShowCoachForm(true);
  };

  const handleCancelEditCoachQualification = () => {
    setEditingCoachQualId(null);
    setCoachQualForm({ level: '', description: '', payPerHour: '' });
    setShowCoachForm(false);
  };

  const handleArchiveCoachQualification = async (id) => {
    if (!window.confirm('Archive this coach qualification? It will be hidden from all functions.')) return;
    try {
      const response = await auditFetch(`${API_BASE}/coach-qualifications/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSuccess('Coach qualification archived!');
        loadData();
      } else {
        setError('Failed to archive coach qualification');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ==================== DIVISION MAPPINGS ====================

  const handleDivisionMappingChange = (divisionName, ageGroup) => {
    setDivisionMappings(prev => ({ ...prev, [divisionName]: ageGroup }));
  };

  const handleSaveDivisionMappings = async () => {
    try {
      setDivisionSaveStatus('saving...');
      const mappings = Object.entries(divisionMappings).map(([divisionName, ageGroup]) => ({
        divisionName,
        ageGroup
      }));

      const response = await auditFetch(`${API_BASE}/division-mappings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings })
      });

      if (response.ok) {
        setSuccess('Division mappings saved successfully!');
        setDivisionSaveStatus('');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save division mappings');
        setDivisionSaveStatus('');
      }
    } catch (err) {
      setError('Error saving division mappings: ' + err.message);
      setDivisionSaveStatus('');
    }
  };

  return (
    <div className="qualifications-page">
      <div className="qualifications-tabs">
        {canView('admin', 'referee-qualifications') && (
          <button className={activeTab === 'referee-qualifications' ? 'active' : ''} onClick={() => setActiveTab('referee-qualifications')}>Referee Qualifications</button>
        )}
        {canView('admin', 'bench-qualifications') && (
          <button className={activeTab === 'bench-qualifications' ? 'active' : ''} onClick={() => setActiveTab('bench-qualifications')}>Bench Official Qualifications</button>
        )}
        {canView('admin', 'court-qualifications') && (
          <button className={activeTab === 'court-qualifications' ? 'active' : ''} onClick={() => setActiveTab('court-qualifications')}>Court Official Qualifications</button>
        )}
        {canView('admin', 'coach-qualifications') && (
          <button className={activeTab === 'coach-qualifications' ? 'active' : ''} onClick={() => setActiveTab('coach-qualifications')}>Coach Qualifications</button>
        )}
        {canView('admin', 'division-age-groups') && (
          <button className={activeTab === 'division-mappings' ? 'active' : ''} onClick={() => setActiveTab('division-mappings')}>Division Age Groups</button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {loading && <div className="loading">Loading...</div>}

      {/* REFEREE QUALIFICATIONS TAB */}
      {activeTab === 'referee-qualifications' && (
        <div className="admin-section">
          {showRefForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowRefForm(false); handleCancelEditRefQualification(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingRefQualId ? 'Edit Referee Qualification' : 'Add Referee Qualification'}</h3>
                  <button onClick={() => { setShowRefForm(false); handleCancelEditRefQualification(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddRefQualification} className="admin-form">
                  <div className="form-group">
                    <label>Level:</label>
                    <input type="text" placeholder="e.g., Level 1, Level 2" value={refQualForm.level} onChange={(e) => setRefQualForm({ ...refQualForm, level: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <input type="text" placeholder="e.g., Junior Referee" value={refQualForm.description} onChange={(e) => setRefQualForm({ ...refQualForm, description: e.target.value })} required />
                  </div>
                  <div className="pay-rate-section-title">Pay Rates by Age Band <span style={{ fontWeight: 400, color: '#666', fontSize: '0.8rem' }}>(leave blank = not assignable to that band)</span></div>
                  <div className="age-group-pay-grid">
                    {REF_BANDS.map(b => (
                      <div key={`ref-${b.col}`} className="age-group-pay-column">
                        <label>{b.label}</label>
                        <input type="number" step="0.01" placeholder="(blank)" value={refQualForm.payRates[b.col] ?? ''} onChange={(e) => setRefQualForm({ ...refQualForm, payRates: { ...refQualForm.payRates, [b.col]: e.target.value } })} />
                      </div>
                    ))}
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">{editingRefQualId ? 'Update Referee Qualification' : 'Add Referee Qualification'}</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowRefForm(false); handleCancelEditRefQualification(); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedRefQuals} onChange={(e) => setShowArchivedRefQuals(e.target.checked)} />
              Show Archived
            </label>
            <button className="btn-submit" onClick={() => { handleCancelEditRefQualification(); setShowRefForm(true); }}>+ Add Qualification</button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Level</th><th>Description</th><th>Actions</th></tr></thead>
            <tbody>
              {refereeQualifications.map(qual => (
                <tr key={qual.id} style={qual.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{qual.level}{qual.isArchived ? ' (Archived)' : ''}</td>
                  <td>{qual.description}</td>
                  <td>
                    {!qual.isArchived && <button className="btn-edit" onClick={() => handleEditRefQualification(qual)}>Edit</button>}
                    {!qual.isArchived && <button className="btn-delete" onClick={() => handleArchiveRefQualification(qual.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* BENCH OFFICIAL QUALIFICATIONS TAB */}
      {activeTab === 'bench-qualifications' && (
        <div className="admin-section">
          {showBenchForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowBenchForm(false); handleCancelEditBenchQualification(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingBenchQualId ? 'Edit Bench Official Qualification' : 'Add Bench Official Qualification'}</h3>
                  <button onClick={() => { setShowBenchForm(false); handleCancelEditBenchQualification(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddBenchQualification} className="admin-form">
                  <div className="form-group">
                    <label>Level:</label>
                    <input type="text" placeholder="e.g., Level 1, Level 2" value={benchQualForm.level} onChange={(e) => setBenchQualForm({ ...benchQualForm, level: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <input type="text" placeholder="e.g., Bench Official" value={benchQualForm.description} onChange={(e) => setBenchQualForm({ ...benchQualForm, description: e.target.value })} required />
                  </div>
                  <div className="pay-rate-section-title">Pay Rates by Game Band <span style={{ fontWeight: 400, color: '#666', fontSize: '0.8rem' }}>(leave blank = not assignable to that band)</span></div>
                  <div className="age-group-pay-grid">
                    {BENCH_BANDS.map(b => (
                      <div key={`bench-${b.col}`} className="age-group-pay-column">
                        <label>{b.label}</label>
                        <input type="number" step="0.01" placeholder="(blank)" value={benchQualForm.payRates[b.col] ?? ''} onChange={(e) => setBenchQualForm({ ...benchQualForm, payRates: { ...benchQualForm.payRates, [b.col]: e.target.value } })} />
                      </div>
                    ))}
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">{editingBenchQualId ? 'Update Bench Official Qualification' : 'Add Bench Official Qualification'}</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowBenchForm(false); handleCancelEditBenchQualification(); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedBenchQuals} onChange={(e) => setShowArchivedBenchQuals(e.target.checked)} />
              Show Archived
            </label>
            <button className="btn-submit" onClick={() => { handleCancelEditBenchQualification(); setShowBenchForm(true); }}>+ Add Qualification</button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Level</th><th>Description</th><th>Actions</th></tr></thead>
            <tbody>
              {benchQualifications.map(qual => (
                <tr key={qual.id} style={qual.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{qual.level}{qual.isArchived ? ' (Archived)' : ''}</td>
                  <td>{qual.description}</td>
                  <td>
                    {!qual.isArchived && <button className="btn-edit" onClick={() => handleEditBenchQualification(qual)}>Edit</button>}
                    {!qual.isArchived && <button className="btn-delete" onClick={() => handleArchiveBenchQualification(qual.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* COURT OFFICIAL QUALIFICATIONS TAB */}
      {activeTab === 'court-qualifications' && (
        <div className="admin-section">
          {showCourtForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowCourtForm(false); handleCancelEditCourtQualification(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingCourtQualId ? 'Edit Court Official Qualification' : 'Add Court Official Qualification'}</h3>
                  <button onClick={() => { setShowCourtForm(false); handleCancelEditCourtQualification(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddCourtQualification} className="admin-form">
                  <div className="form-group">
                    <label>Level:</label>
                    <input type="text" placeholder="e.g., Level 1, Level 2" value={courtQualForm.level} onChange={(e) => setCourtQualForm({ ...courtQualForm, level: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <input type="text" placeholder="e.g., Court Official" value={courtQualForm.description} onChange={(e) => setCourtQualForm({ ...courtQualForm, description: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Pay ($):</label>
                    <input type="number" step="0.01" placeholder="0.00" value={courtQualForm.pay} onChange={(e) => setCourtQualForm({ ...courtQualForm, pay: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">{editingCourtQualId ? 'Update Court Official Qualification' : 'Add Court Official Qualification'}</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowCourtForm(false); handleCancelEditCourtQualification(); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedCourtQuals} onChange={(e) => setShowArchivedCourtQuals(e.target.checked)} />
              Show Archived
            </label>
            <button className="btn-submit" onClick={() => { handleCancelEditCourtQualification(); setShowCourtForm(true); }}>+ Add Qualification</button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Level</th><th>Description</th><th>Pay</th><th>Actions</th></tr></thead>
            <tbody>
              {courtQualifications.map(qual => (
                <tr key={qual.id} style={qual.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{qual.level}{qual.isArchived ? ' (Archived)' : ''}</td>
                  <td>{qual.description}</td>
                  <td>${qual.pay}</td>
                  <td>
                    {!qual.isArchived && <button className="btn-edit" onClick={() => handleEditCourtQualification(qual)}>Edit</button>}
                    {!qual.isArchived && <button className="btn-delete" onClick={() => handleArchiveCourtQualification(qual.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* COACH QUALIFICATIONS TAB */}
      {activeTab === 'coach-qualifications' && (
        <div className="admin-section">
          {showCoachForm && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowCoachForm(false); handleCancelEditCoachQualification(); }}>
              <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>{editingCoachQualId ? 'Edit Coach Qualification' : 'Add Coach Qualification'}</h3>
                  <button onClick={() => { setShowCoachForm(false); handleCancelEditCoachQualification(); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                </div>
                <form onSubmit={handleAddCoachQualification} className="admin-form">
                  <div className="form-group">
                    <label>Level:</label>
                    <input type="text" placeholder="e.g., Level 1, Level 2" value={coachQualForm.level} onChange={(e) => setCoachQualForm({ ...coachQualForm, level: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Description:</label>
                    <input type="text" placeholder="e.g., Head Coach" value={coachQualForm.description} onChange={(e) => setCoachQualForm({ ...coachQualForm, description: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Pay Per Hour ($):</label>
                    <input type="number" step="0.01" placeholder="0.00" value={coachQualForm.payPerHour} onChange={(e) => setCoachQualForm({ ...coachQualForm, payPerHour: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="submit" className="btn-submit">{editingCoachQualId ? 'Update Coach Qualification' : 'Add Coach Qualification'}</button>
                    <button type="button" className="btn-cancel" onClick={() => { setShowCoachForm(false); handleCancelEditCoachQualification(); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              <input type="checkbox" checked={showArchivedCoachQuals} onChange={(e) => setShowArchivedCoachQuals(e.target.checked)} />
              Show Archived
            </label>
            <button className="btn-submit" onClick={() => { handleCancelEditCoachQualification(); setShowCoachForm(true); }}>+ Add Qualification</button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Level</th><th>Description</th><th>Pay/Hour</th><th>Actions</th></tr></thead>
            <tbody>
              {coachQualifications.map(qual => (
                <tr key={qual.id} style={qual.isArchived ? { color: '#999', backgroundColor: '#f5f5f5' } : {}}>
                  <td>{qual.level}{qual.isArchived ? ' (Archived)' : ''}</td>
                  <td>{qual.description}</td>
                  <td>${qual.payPerHour}</td>
                  <td>
                    {!qual.isArchived && <button className="btn-edit" onClick={() => handleEditCoachQualification(qual)}>Edit</button>}
                    {!qual.isArchived && <button className="btn-delete" onClick={() => handleArchiveCoachQualification(qual.id)}>Archive</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DIVISION MAPPINGS TAB */}
      {activeTab === 'division-mappings' && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Map each division to a pay group. This determines which pay rate band is used when calculating staff payments.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead><tr><th>Division Name</th><th>Age Group</th></tr></thead>
                <tbody>
                  {divisions.map((division) => (
                    <tr key={division.name}>
                      <td>{division.name}</td>
                      <td>
                        <select
                          value={payGroupValue(divisionMappings[division.name])}
                          onChange={(e) => handleDivisionMappingChange(division.name, e.target.value)}
                          style={{ padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                          {PAY_GROUP_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleSaveDivisionMappings}
              className="btn-submit"
              style={{ marginTop: '20px' }}
              disabled={divisionSaveStatus === 'saving...'}
            >
              {divisionSaveStatus === 'saving...' ? 'Saving...' : 'Save Division Mappings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
